import {Component, EventEmitter, Input, Output} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {Select} from '@core/components/select/select';
import {Switch} from '@core/components/switch/switch';
import {EmployeeRow, GroupItem} from '@core/types';

@Component({
  selector: 'core-group-widget',
  imports: [
    Switch,
    Select,
    FormsModule
  ],
  templateUrl: './group-widget.html',
  styleUrl: './group-widget.scss',
  standalone: true,
  host: {
    style: 'padding: 1.25rem;display: flex;flex-direction: column;width: 100%;border-radius:1rem;gap: 1rem;',
    class: 'bg-neutral'
  }
})
export class GroupWidget {
  @Input() group: GroupItem | null = null;
  @Input() availableEmployees: EmployeeRow[] = [];
  @Input() isSaving = false;
  @Input() feedbackMessage = '';
  @Input() feedbackTone: 'success' | 'error' | null = null;
  @Output() updateGroup = new EventEmitter<{id: string | number; description?: string; function?: string;}>();
  @Output() setManager = new EventEmitter<{id: string | number; employeeId: string | number}>();
  @Output() updateMembers = new EventEmitter<{id: string | number; employeeIds: Array<string | number>}>();
  @Output() viewDetail = new EventEmitter<GroupItem>();
  isEditable = false;
  selectedEmployeeId: string | number | null = null;
  private lastDescription = '';
  private lastFunction = '';
  private lastManagerId: string | number | null = null;

  ngOnChanges() {
    this.syncSnapshot();
  }

  get description(): string {
    return this.group?.description || '';
  }

  set description(value: string) {
    if (!this.group) return;
    this.group.description = value;
  }

  get roleFunction(): string {
    return this.group?.function || '';
  }

  set roleFunction(value: string) {
    if (!this.group) return;
    this.group.function = value;
  }

  get managerId(): string | number | null {
    return this.group?.manager?.id || null;
  }

  get managerOptions() {
    return this.employees.map(member => ({
      label: member.name,
      value: member.id
    }));
  }

  get availableEmployeeOptions() {
    return this.availableEmployees
      .filter(employee => !this.employees.some(member => this.sameId(member.id, employee.id)))
      .map(employee => ({
        label: employee.name,
        value: employee.id
      }));
  }

  set managerId(value: string | number | null) {
    if (!this.group) return;
    const employee = this.employees.find(member => this.sameId(member.id, value));
    this.group.manager = employee || null;
  }

  handleBlur() {
    if (!this.group || this.isSaving) return;

    const nextDescription = (this.group.description || '').trim();
    const nextFunction = (this.group.function || '').trim();
    if (nextDescription === this.lastDescription && nextFunction === this.lastFunction) {
      return;
    }

    this.group.description = nextDescription;
    this.group.function = nextFunction;
    this.lastDescription = nextDescription;
    this.lastFunction = nextFunction;
    this.updateGroup.emit({
      id: this.group.id,
      description: this.group.description,
      function: this.group.function
    });
  }

  handleManagerChange(value: string | number | null) {
    if (!this.group || this.isSaving || value === null || value === undefined || value === '') return;
    if (this.sameId(value, this.lastManagerId)) return;

    this.managerId = value;
    this.lastManagerId = value;
    this.setManager.emit({
      id: this.group.id,
      employeeId: value
    });
  }

  get employees() {
    return this.group?.employees || [];
  }

  addEmployee() {
    if (!this.group || this.isSaving || this.selectedEmployeeId === null || this.selectedEmployeeId === undefined) return;
    if (this.employees.some(member => this.sameId(member.id, this.selectedEmployeeId))) return;

    this.updateMembers.emit({
      id: this.group.id,
      employeeIds: [...this.employees.map(member => member.id), this.selectedEmployeeId]
    });
    this.selectedEmployeeId = null;
  }

  removeEmployee(employeeId: string | number) {
    if (!this.group || this.isSaving) return;

    const nextEmployeeIds = this.employees
      .filter(member => !this.sameId(member.id, employeeId))
      .map(member => member.id);

    this.updateMembers.emit({
      id: this.group.id,
      employeeIds: nextEmployeeIds
    });
  }

  openDetail() {
    if (!this.group) return;
    this.viewDetail.emit(this.group);
  }

  private syncSnapshot() {
    this.lastDescription = (this.group?.description || '').trim();
    this.lastFunction = (this.group?.function || '').trim();
    this.lastManagerId = this.group?.manager?.id || null;
  }

  private sameId(left: string | number | null | undefined, right: string | number | null | undefined) {
    if (left === null || left === undefined || right === null || right === undefined) {
      return false;
    }

    return String(left) === String(right);
  }
}
